import Vue from "vue";
import axios from "axios";
import Notifications from "vue-notification";
import { NOTIFICATION_TYPE, API_BASE_URL } from "./constants";

Vue.use(Notifications);

/**
 * Get cookie value by name
 */
function getCookie(name) {
  if (typeof document === 'undefined') return null;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop().split(';').shift());
  }
  return null;
}

/**
 * Get the current auth token from cookies
 */
function getAuthToken() {
  const token = getCookie('auth._token.local');
  if (token && token !== 'false') {
    return token;
  }
  return null;
}

// Create axios instance with auth interceptors
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);

export default apiClient;

async function fetchingFactory(fetchFn, options = {}) {
  try {
    const result = fetchFn();
    if (options.successMessage) {
      const title = options.successMessage.title || options.successMessage;
      const text = options.successMessage.text;
      Vue.notify({
        type: NOTIFICATION_TYPE.SUCCESS,
        title,
        text,
      });
    }
    return result;
  } catch (error) {
    let message = "Ops, something goes wrong";
    if (options.errorMessage) {
      message = options.errorMessage;
    }
    Vue.notify({
      type: NOTIFICATION_TYPE.ERROR,
      title: message,
    });
  }
}

function toCamelCase(obj) {
  if (typeof obj !== "object" || obj === null) return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => toCamelCase(item));
  }

  return Object.keys(obj).reduce((acc, key) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    );
    acc[camelKey] = toCamelCase(obj[key]);
    return acc;
  }, {});
}

export const profileGetByProfileId = async (profileId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`/companies/profile/get?profileId=${profileId}`)
      .then((response) => {
        const profile = response.data;
        if (profile.profile_picture === null) {
          profile.profile_picture = `https://eu.ui-avatars.com/api/??size=128&background=FFF5ED&color=9D1A16&name=${profile.firstname}%20${profile.lastname}`;
        }
        return profile;
      })
  );
};

export const getAvailableStatusesById = async () => {
  return await fetchingFactory(() =>
    apiClient.get(`/companies/profile/status/read`).then((response) => {
      return response.data.reduce(
        (acc, status) => ({ ...acc, [status.status_id]: status.status_name }),
        {}
      );
    })
  );
};

export const getUserDocuments = async (profileId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`/companies/profile/document/read?id=${profileId}`)
      .then((response) => response.data)
  );
};

export const deleteDocumentById = async (documentId) => {
  return await fetchingFactory(
    () =>
      apiClient
        .post("/pm/service/document/delete", {
          document_id: documentId,
        })
        .then((response) => response.data),
    { successMessage: "Verwijderd!" }
  );
};

export const resetPasswordRequest = async (email) => {
  return await fetchingFactory(
    () =>
      apiClient
        .post("/companies/user/sendResetEmail", {
          email,
        })
        .then((response) => response.data),
    {
      successMessage: {
        title: `Mail verstuurd`,
        text: `De aanvraag is verstuurd via de mail!`,
      },
    }
  );
};

export const updateProfileById = async (profileId, data) => {
  return await fetchingFactory(
    () =>
      apiClient
        .post(`/companies/profile/update`, {
          ...toCamelCase(data),
          profileId,
        })
        .then((response) => response.data),
    { successMessage: "bijgewerkt!" }
  );
};

export const getRelation = async (relationId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`/pm/relation/read?relationId=${relationId}`)
      .then((response) => response.data)
  );
};

export const getRelationContacts = async (relationId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`/pm/relation/contact/read?id=${relationId}`)
      .then((response) => response.data)
  );
};

export const getRelationDocuments = async (relationId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`/pm/relation/document/read?id=${relationId}`)
      .then((response) => response.data)
  );
};

export const archiveRelation = async (relationId) => {
  return await fetchingFactory(
    () =>
      apiClient
        .post(`/pm/relation/archive`, {
          relationId,
        })
        .then((response) => response.data),
    { successMessage: "Gearchiveerd!" }
  );
};

export const getCompanyResources = async (companyId, schedule = undefined) => {
  return await fetchingFactory(() =>
    apiClient
      .get(
        `/pm/schedule/get-resources?companyId=${companyId}${
          schedule ? `&schedule=${schedule}` : ""
        }`
      )
      .then((response) => response.data)
  );
};

export const getShiftStatuses = async () => {
  return await fetchingFactory(() =>
    apiClient
      .get(`/pm/shift/status/get-list`)
      .then((response) => response.data)
  );
};

export const checkChanges = async (companyId, dateTime) => {
  return await fetchingFactory(() =>
    apiClient
      .post("/pm/schedule/checkChanges", {
        companyId,
        dateTime,
      })
      .then((response) => response.data)
  );
};

export const bulkEditStatuses = async (bulkArray, statusId) => {
  return await fetchingFactory(
    () =>
      apiClient
        .post("/pm/schedule/bulk-action", {
          bulkArray,
          statusId,
        })
        .then((response) => response.data),
    { successMessage: "Bulk toegepast" }
  );
};

export const postNotification = async (user_id, title, content) => {
  return await fetchingFactory(
    () =>
      apiClient.post(`/notification/create-notification`, {
        user_id,
        title,
        content,
      }),
    { successMessage: "Notificatie gestuurd" }
  );
};

export const getCompanyTags = async (companyId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`/pm/label/read?companyId=${companyId}`)
      .then((response) => response.data)
  );
};

export const getCompanyRelations = async (companyId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`/pm/relation/read?companyId=${companyId}`)
      .then((response) => response.data)
  );
};

// Subcontractor API functions
export const getSubcontractors = async (companyId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`subcontractors?company_id=${companyId}`)
      .then((response) => response.data.data)
  );
};

export const getSubcontractor = async (subcontractorId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`subcontractors/${subcontractorId}`)
      .then((response) => response.data.data)
  );
};

export const createSubcontractor = async (subcontractorData) => {
  return await fetchingFactory(() =>
    apiClient
      .post('subcontractors', subcontractorData)
      .then((response) => response.data),
    {
      successMessage: {
        title: "Onderaannemer aangemaakt",
        text: "De onderaannemer is succesvol aangemaakt."
      }
    }
  );
};

export const updateSubcontractor = async (subcontractorId, subcontractorData, currentUserId = null) => {
  const updateData = currentUserId ? { ...subcontractorData, current_user_id: currentUserId } : subcontractorData;

  return await fetchingFactory(() =>
    apiClient
      .put(`subcontractors/${subcontractorId}`, updateData)
      .then((response) => response.data),
    {
      successMessage: {
        title: "Onderaannemer bijgewerkt",
        text: "De onderaannemer is succesvol bijgewerkt."
      }
    }
  );
};

export const deleteSubcontractor = async (subcontractorId, currentUserId = null) => {
  const deleteData = currentUserId ? { current_user_id: currentUserId } : {};

  return await fetchingFactory(() =>
    apiClient
      .delete(`subcontractors/${subcontractorId}`, {
        data: deleteData
      })
      .then((response) => response.data),
    {
      successMessage: {
        title: "Onderaannemer verwijderd",
        text: "De onderaannemer is succesvol verwijderd."
      }
    }
  );
};

export const getSubcontractorEmployees = async (subcontractorId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`subcontractors/${subcontractorId}/employees`)
      .then((response) => response.data.data)
  );
};

export const getSubcontractorEmployeesBulk = async (subcontractorIds = []) => {
  if (!Array.isArray(subcontractorIds) || subcontractorIds.length === 0) {
    return {};
  }

  return await fetchingFactory(() =>
    apiClient
      .get('subcontractors/employees', {
        params: { ids: subcontractorIds },
      })
      .then((response) => response.data.data || {})
  );
};

export const getSubcontractorServices = async (subcontractorId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`subcontractors/${subcontractorId}/services`)
      .then((response) => response.data.data)
  );
};

export const getDocumentByIdAndPid = async (documentId, pid) => {
  return await fetchingFactory(() =>
    apiClient
      .get(
        `/pm/service/document/read?id=${documentId}${
          pid ? `&Pid=${pid}` : ""
        }`
      )
      .then((response) => response.data)
  );
};

export const getCompanyEvents = async (
  companyId,
  startDateTime,
  endDateTime,
  modifiedAt = undefined,
  serviceId = undefined
) => {
  return await fetchingFactory(() =>
    apiClient
      .post(
        `/events-new-query?companyId=${companyId}&startDate=${startDateTime}&endDate=${endDateTime}${
          serviceId ? `&serviceId=${serviceId}` : ""
        }${modifiedAt ? `&modifiedAt=${modifiedAt}` : ""}`, { }
      )
      .then((response) => response.data)
  );
};

export const resendConfirmationEmail = async (user_id) => {
  return await fetchingFactory(
    () =>
      apiClient
        .get(`/account/resend-email?user_id=${user_id}`)
        .then((response) => response.data),
    {
      successMessage: {
        title: `Mail verstuurd`,
        text: `De aanvraag is verstuurd via de mail!`,
      },
    }
  );
};

export const getCompanyProfiles = async (userId) => {
  return await fetchingFactory(() =>
    apiClient
      .get(`/companies/profile/get-all?userId=${userId}`)
      .then((response) => response.data)
  );
};

export const getPermissions = async (profileId) => {
  return await fetchingFactory(() =>
    apiClient
      .post("/companies/profile/get-permissions", { profileId })
      .then((response) => response.data)
  );
};

export const createLabel = async (labelName, companyId) => {
  return await fetchingFactory(
    () =>
      apiClient
        .post("/pm/label/create", { labelName, companyId })
        .then((response) => response.data),
    { successMessage: "Label aangemaakt!" }
  );
};

export const updateLabel = async (labelId, labelName) => {
  return await fetchingFactory(
    () =>
      apiClient
        .post("/pm/label/update", { labelId, labelName })
        .then((response) => response.data),
    { successMessage: "Label bijgewerkt!" }
  );
};

export const deleteLabel = async (labelId) => {
  return await fetchingFactory(
    () =>
      apiClient
        .post("/pm/label/delete", { labelId })
        .then((response) => response.data),
    { successMessage: "Label verwijderd!" }
  );
};
